/**
 * My notifications, from the account menu: per category, whether the
 * signed-in member gets its email at once, in a digest, or not at all.
 * Account mail (setup links, resets, approval requests) is never listed: it
 * always arrives. Applies when the member is mailed as a team member, not to
 * the on-call addresses.
 */
import { useEffect, useState } from "react";
import { put, useApi } from "../../kit/api";
import { Button, Seg, Skeleton, Banner } from "../../kit/ui";
import { Dialog, useToast } from "../../kit/overlays";

type Topic = { key: string; label: string; events: { key: string; label: string; class: string }[] };
type Data = { topics: Topic[]; prefs: Record<string, string> };
type Mode = "default" | "immediate" | "digest" | "off";

export function MyNotifications({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, error, reload } = useApi<Data>(open ? "/api/notifications/me" : null);
  const toast = useToast();
  const [draft, setDraft] = useState<Record<string, Mode> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) setDraft(null);
    else if (data && !draft) setDraft(Object.fromEntries(data.topics.map((t) => [t.key, (data.prefs[t.key] as Mode) ?? "default"])));
  }, [open, data, draft]);
  const changed = data && draft ? data.topics.filter((t) => (draft[t.key] ?? "default") !== ((data.prefs[t.key] as Mode) ?? "default")) : [];
  const save = () => {
    if (!draft) return;
    setBusy(true);
    put("/api/notifications/me", { prefs: Object.fromEntries(changed.map((t) => [t.key, draft[t.key] === "default" ? null : draft[t.key]])) })
      .then(() => (toast("Your notification choices are saved."), reload(), onClose()))
      .catch((e: Error) => toast(e.message, "err"))
      .finally(() => setBusy(false));
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={busy}
      size="wide"
      icon="mail"
      title="My notifications"
      lead="How you get the Brain's email when it comes to you as a team member. Setup links, resets and approval requests always arrive."
      note={changed.length ? `${changed.length} unsaved ${changed.length === 1 ? "change" : "changes"}.` : "Digest groups your notices into one email an hour or a day."}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button kind="primary" busy={busy} disabled={!changed.length} onClick={save}>Save changes</Button>
        </>
      }
    >
      {!data || !draft ? (
        error ? <Banner tone="red" icon="error-circle">{error.message}</Banner> : <Skeleton rows={6} />
      ) : (
        <table className="t compact" style={{ width: "100%" }}>
          <thead>
            <tr><th>Category</th><th>Email</th></tr>
          </thead>
          <tbody>
            {data.topics.map((t) => (
              <tr key={t.key}>
                <td>
                  <span className="cell2">
                    <b>{t.label}</b>
                    <small title={t.events.map((e) => e.label).join(", ")}>{t.events.slice(0, 3).map((e) => e.label).join(", ")}{t.events.length > 3 ? ` and ${t.events.length - 3} more` : ""}</small>
                  </span>
                </td>
                <td style={{ width: 380 }}>
                  <Seg<Mode> value={draft[t.key] ?? "default"} onChange={(v) => setDraft({ ...draft, [t.key]: v })} options={[["default", "As set"], ["immediate", "Immediately"], ["digest", "Digest"], ["off", "Off"]]} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Dialog>
  );
}
